import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaWhatsapp, FaInstagram, FaEnvelope, FaClock, FaMapMarkerAlt } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

// Estilos usando styled-components com visual ultra-moderno
const AppContainer = styled.div`
  font-family: 'Poppins', sans-serif;
  color: #333;
  line-height: 1.6;
  background-color: var(--light-bg);
  overflow-x: hidden;
  
  --primary-color: #5a9a94;
  --secondary-color: #84b59f;
  --light-bg: #f9fafb;
  --dark-bg: #333;
  --gradient-primary: linear-gradient(135deg, #5a9a94 0%, #84b59f 100%);
  --gradient-secondary: linear-gradient(135deg, #84b59f 0%, #90c3be 100%);
  
  * {
    box-sizing: border-box;
  }
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const Header = styled.header`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 15px 40px;
  position: fixed;
  width: 100%;
  z-index: 1000;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 12px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 15px;
  }
`;

const Logo = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.5px;
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

// Menu mobile para substituir a barra de navegação em telas pequenas
const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--primary-color);
  z-index: 1010;
  
  @media (max-width: 768px) {
    display: block;
    position: absolute;
    top: 15px;
    right: 20px;
  }
`;

const MobileMenu = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.98);
    z-index: 1005;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: ${props => props.$isOpen ? 'fadeIn 0.3s ease' : 'none'};
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const MobileNavLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-size: 1.2rem;
  margin: 15px 0;
  font-weight: 500;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: var(--gradient-primary);
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const NavBar = styled.nav`
  display: flex;
  gap: 30px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: #666;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  position: relative;
  padding-bottom: 5px;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: 0;
    left: 0;
    background: var(--gradient-primary);
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: var(--primary-color);
    
    &:after {
      width: 100%;
    }
  }
`;

const HeroSection = styled.section`
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0 10%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #f0f7f6 0%, #e0eeeb 100%);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 70% 50%, rgba(132, 181, 159, 0.1) 0%, rgba(90, 154, 148, 0.05) 50%);
    z-index: 0;
  }
  
  @media (max-width: 768px) {
    padding: 0 6%;
    align-items: center;
    text-align: center;
    padding-top: 80px;
    height: auto;
    min-height: 100vh;
  }
`;

const HeroBgSVG = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 0;
  opacity: 0.6;
`;

const HeroContent = styled.div`
  max-width: 600px;
  animation: fadeIn 1.5s ease-out;
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  padding: 45px;
  border-radius: 24px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.7);
  transform: translateY(0);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.15);
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @media (max-width: 768px) {
    padding: 30px;
    max-width: 90%;
  }
  
  @media (max-width: 480px) {
    padding: 25px;
    width: 100%;
  }
`;

const HeroTitle = styled.h2`
  font-size: 3.2rem;
  font-weight: 800;
  margin-bottom: 20px;
  line-height: 1.2;
  background: linear-gradient(135deg, #5a9a94 0%, #84b59f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.5px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #5a9a94, #84b59f);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    
    &:after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin: 25px 0 40px;
  color: #444;
  font-weight: 400;
  line-height: 1.8;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin: 30px 0 30px;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.6;
    margin: 25px 0 25px;
  }
`;

const Button = styled.a`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: white;
  padding: 16px 32px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  font-size: 1.05rem;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.4s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  letter-spacing: 1px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 14px 28px;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 12px 24px;
    font-size: 0.95rem;
    width: 100%;
    max-width: 250px;
  }
`;

const HeroDecoration = styled.div`
  position: absolute;
  top: 50%;
  right: 12%;
  transform: translateY(-50%);
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, rgba(90, 154, 148, 0.15), rgba(132, 181, 159, 0.15));
  border-radius: 50%;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 992px) {
    display: none;
  }
`;

const HeroDecorationInner = styled.div`
  width: 250px;
  height: 250px;
  background: linear-gradient(135deg, rgba(132, 181, 159, 0.2), rgba(90, 154, 148, 0.2));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroDecorationCore = styled.div`
  width: 150px;
  height: 150px;
  background: linear-gradient(135deg, rgba(90, 154, 148, 0.25), rgba(132, 181, 159, 0.25));
  border-radius: 50%;
  animation: pulse 3s infinite ease-in-out;
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`;

const Section = styled.section`
  padding: 120px 10%;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 80px 6%;
  }
  
  @media (max-width: 480px) {
    padding: 60px 5%;
  }
`;

const ServicesSection = styled(Section)`
  background-color: var(--light-bg);
  position: relative;
  overflow: hidden;
`;

const ServicesBgSVG = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 0;
  opacity: 0.4;
`;

const SectionHeading = styled.div`
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
`;

const SectionSubtitle = styled.span`
  text-transform: uppercase;
  letter-spacing: 3px;
  font-size: 0.8rem;
  color: var(--primary-color);
  font-weight: 600;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin: 15px 0 0;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
`;

const ServicesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 30px;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  width: 70px;
  height: 70px;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-bottom: 25px;
  color: white;
  font-size: 1.8rem;
  box-shadow: 0 10px 20px rgba(90, 154, 148, 0.3);
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 1rem;
  line-height: 1.7;
`;

const AboutSection = styled(Section)`
  background: linear-gradient(135deg, #f0f7f6, #e6f3f0);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 60px;
  position: relative;
  overflow: hidden;

  @media (max-width: 992px) {
    flex-direction: column;
    gap: 40px;
  }
  
  @media (max-width: 480px) {
    gap: 30px;
  }
`;

const AboutBgSVG = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 0;
  opacity: 0.4;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 450px;
  height: 550px;
  z-index: 1;
  
  @media (max-width: 768px) {
    width: 350px;
    height: 430px;
  }
  
  @media (max-width: 480px) {
    width: 280px;
    height: 340px;
  }
`;

const AboutImage = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  background-color: #e6f3f0;
  transition: all 0.5s ease;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.5s ease;
  }
  
  &:after {
    content: ${props => props.$hasImage ? '""' : '"Foto da Psicóloga"'};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #666;
  }
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
  }
`;

const AboutImageDecoration1 = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: var(--gradient-primary);
  z-index: -1;
  top: -30px;
  left: -30px;
  opacity: 0.1;
`;

const AboutImageDecoration2 = styled.div`
  position: absolute;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: var(--gradient-secondary);
  z-index: -1;
  bottom: -20px;
  right: -20px;
  opacity: 0.1;
`;

const AboutContent = styled.div`
  max-width: 500px;
  position: relative;
  z-index: 1;
`;

const AboutTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 20px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const AboutText = styled.p`
  margin-bottom: 25px;
  color: #555;
  line-height: 1.8;
  font-size: 1.05rem;
`;

const ContactSection = styled(Section)`
  background: var(--gradient-primary);
  color: white;
  text-align: center;
`;

const ContactTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 20px;
  color: white;
`;

const InfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    margin-top: 40px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 30px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const InfoIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 20px;
  opacity: 0.9;
`;

const InfoTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 15px;
  font-weight: 500;
`;

const InfoText = styled.p`
  font-size: 1rem;
  opacity: 0.9;
`;

const Footer = styled.footer`
  background-color: var(--dark-bg);
  color: white;
  padding: 60px 10% 30px;
  text-align: center;
`;

const FooterLogo = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 25px;
  background: var(--gradient-secondary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FooterText = styled.p`
  max-width: 600px;
  margin: 0 auto 30px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  line-height: 1.8;
`;

const SocialIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 30px 0;
  
  a {
    color: white;
    width: 50px;
    height: 50px;
    font-size: 1.3rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    border: 1px solid rgba(255, 255, 255, 0.1);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: translateY(-5px);
    }
  }
`;

const Copyright = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
  margin-top: 30px;
`;

// Adicione estes novos componentes estilizados após os existentes
const TestimonialsSection = styled(Section)`
  background-color: #f2f8f7;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const TestimonialsBgSVG = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 0;
  opacity: 0.4;
`;

const TestimonialsContainer = styled.div`
  display: flex;
  gap: 30px;
  margin-top: 50px;
  overflow-x: auto;
  padding: 20px 0 40px;
  position: relative;
  z-index: 1;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #e6f3f0;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--primary-color);
    border-radius: 10px;
  }
  
  @media (max-width: 768px) {
    margin-top: 30px;
    gap: 20px;
    
    &::-webkit-scrollbar {
      height: 6px;
    }
  }
`;

const TestimonialCard = styled.div`
  background: white;
  min-width: 320px;
  max-width: 320px;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  text-align: left;
  position: relative;
  
  &:before {
    content: '"';
    font-size: 120px;
    font-family: Georgia, serif;
    position: absolute;
    top: -20px;
    left: 20px;
    color: var(--primary-color);
    opacity: 0.1;
  }
  
  @media (max-width: 768px) {
    min-width: 280px;
    max-width: 280px;
    padding: 25px;
  }
  
  @media (max-width: 480px) {
    min-width: 260px;
    max-width: 260px;
    padding: 20px;
    
    &:before {
      font-size: 100px;
    }
  }
`;

const TestimonialText = styled.p`
  font-style: italic;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
  line-height: 1.7;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  margin-top: auto;
`;

const TestimonialAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #f0f0f0;
  margin-right: 15px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TestimonialInfo = styled.div``;

const TestimonialName = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
`;

const TestimonialTitle = styled.p`
  margin: 3px 0 0;
  font-size: 0.85rem;
  color: #666;
`;

const SpecialtiesSection = styled(Section)`
  background: linear-gradient(135deg, #e6f3f0, #f0f7f6);
  position: relative;
  overflow: hidden;
`;

const SpecialtiesBgSVG = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 0;
  opacity: 0.4;
`;

const SpecialtiesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 50px;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const SpecialtyCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.8);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const SpecialtyIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--gradient-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  color: white;
  font-size: 1.5rem;
  box-shadow: 0 10px 20px rgba(90, 154, 148, 0.3);
`;

const SpecialtyTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 15px;
  color: #333;
`;

const SpecialtyDescription = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.7;
`;

const FaqSection = styled(Section)`
  background-color: white;
  position: relative;
  overflow: hidden;
`;

const FaqContainer = styled.div`
  max-width: 800px;
  margin: 50px auto 0;
  position: relative;
  z-index: 1;
`;

const FaqItem = styled.div`
  margin-bottom: 20px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const FaqQuestion = styled.div`
  padding: 20px 30px;
  background: var(--light-bg);
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  
  &:after {
    content: '+';
    font-size: 1.5rem;
    color: var(--primary-color);
  }
  
  ${props => props.$isOpen && `
    &:after {
      content: '-';
    }
  `}
  
  @media (max-width: 768px) {
    padding: 18px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 15px;
    font-size: 0.95rem;
  }
`;

const FaqAnswer = styled.div`
  padding: ${props => props.$isOpen ? '20px 30px' : '0 30px'};
  max-height: ${props => props.$isOpen ? '300px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  line-height: 1.7;
  opacity: ${props => props.$isOpen ? '1' : '0'};
`;

const EducationSection = styled(Section)`
  background: linear-gradient(135deg, #f0f7f6, #e6f3f0);
  position: relative;
  overflow: hidden;
`;

const EducationContainer = styled.div`
  position: relative;
  max-width: 800px;
  margin: 50px auto 0;
  padding-left: 40px;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 10px;
    width: 2px;
    height: 100%;
    background: var(--primary-color);
    opacity: 0.3;
  }
`;

const EducationItem = styled.div`
  position: relative;
  margin-bottom: 40px;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -40px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--gradient-primary);
    box-shadow: 0 0 0 5px rgba(90, 154, 148, 0.2);
  }
`;

const EducationYear = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 5px;
  color: var(--primary-color);
`;

const EducationDegree = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 10px;
`;

const EducationInstitution = styled.div`
  font-size: 1rem;
  margin-bottom: 10px;
  opacity: 0.8;
`;

const EducationDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.7;
  color: #555;
`;

// Adicionar os novos componentes após os existentes
const CtaSection = styled.section`
  background: var(--gradient-primary);
  padding: 80px 10%;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 60px 5%;
  }
  
  @media (max-width: 480px) {
    padding: 50px 20px;
  }
`;

const CtaTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 20px;
  font-weight: 600;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    max-width: 90%;
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    max-width: 100%;
    padding: 0 10px;
    line-height: 1.3;
    word-break: normal;
    overflow-wrap: break-word;
    hyphens: auto;
  }
`;

const CtaText = styled.p`
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 40px;
  opacity: 0.9;
  line-height: 1.8;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    max-width: 90%;
    margin-bottom: 30px;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 25px;
  }
`;

const CtaButton = styled(Button)`
  background: white;
  color: var(--primary-color);
  font-weight: 600;
  padding: 15px 40px;
  font-size: 1.1rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }
`;

// Adicionar o componente de botão flutuante após os componentes estilizados existentes
const WhatsAppButton = styled.a`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #25D366, #128C7E);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  text-decoration: none;
  z-index: 1000;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1) translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
  
  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #25D366, #128C7E);
    border-radius: 50%;
    opacity: 0.4;
    animation: pulse 2s infinite;
    z-index: -1;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.4;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.2;
    }
    100% {
      transform: scale(1);
      opacity: 0.4;
    }
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    bottom: 20px;
    right: 20px;
    font-size: 1.5rem;
  }
`;

const WhatsAppTooltip = styled.span`
  position: absolute;
  background: white;
  color: #333;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 0.9rem;
  white-space: nowrap;
  right: 75px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transform: translateX(20px);
  transition: all 0.3s ease;
  pointer-events: none;
  font-weight: 500;
  
  ${WhatsAppButton}:hover & {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Substituir o ModalOverlay e Modal por uma seção completa
const BookingSection = styled.section`
  padding: 80px 0;
  background: linear-gradient(135deg, #f0f7f6 0%, #e6f3f0 100%);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  overflow-y: auto;
  transform: ${props => props.$isVisible ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.5s ease;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 70% 50%, rgba(132, 181, 159, 0.1) 0%, rgba(90, 154, 148, 0.05) 50%);
    z-index: 0;
  }
`;

const BookingContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 30px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.12);
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    max-width: 90%;
    padding: 25px;
  }
`;

const BookingFormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 1.1rem;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 15px;
  border-radius: 50px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(90, 154, 148, 0.1);
  }
`;

const BackArrow = styled.span`
  font-size: 1.5rem;
`;

const BookingFormTitle = styled.h2`
  font-size: 2rem;
  margin: 0;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
  flex-grow: 1;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

// Adicionando os componentes de formulário que faltam
const BookingForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 0.95rem;
  font-weight: 500;
  color: #444;
`;

const Input = styled.input`
  padding: 12px 15px;
  border-radius: 10px;
  border: 1px solid #ddd;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(90, 154, 148, 0.2);
  }
  
  @media (max-width: 480px) {
    padding: 14px 15px;
    font-size: 16px; /* Evita zoom automático em iOS */
  }
`;

const Select = styled.select`
  padding: 12px 15px;
  border-radius: 10px;
  border: 1px solid #ddd;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(90, 154, 148, 0.2);
  }
  
  @media (max-width: 480px) {
    padding: 14px 15px;
    font-size: 16px; /* Evita zoom automático em iOS */
  }
`;

const Textarea = styled.textarea`
  padding: 12px 15px;
  border-radius: 10px;
  border: 1px solid #ddd;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(90, 154, 148, 0.2);
  }
  
  @media (max-width: 480px) {
    padding: 14px 15px;
    font-size: 16px; /* Evita zoom automático em iOS */
  }
`;

const SubmitButton = styled.button`
  padding: 15px;
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(90, 154, 148, 0.2);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 30px rgba(90, 154, 148, 0.3);
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 20px;
  
  p {
    margin-bottom: 20px;
    font-size: 1.1rem;
    line-height: 1.5;
  }
`;

// Componente para o Widget de Avaliações do Google
const GoogleReviewsWidget = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 5;
  
  iframe {
    width: 100%;
    min-height: 500px;
    border: none;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  }
  
  @media (max-width: 768px) {
    iframe {
      min-height: 600px;
    }
  }
`;

// Adicione estes novos componentes estilizados para feedback do formulário
const FormError = styled.div`
  color: #e74c3c;
  margin-bottom: 15px;
  font-size: 0.9rem;
  background-color: rgba(231, 76, 60, 0.1);
  padding: 10px 15px;
  border-radius: 5px;
  border-left: 4px solid #e74c3c;
`;

const FormSuccess = styled.div`
  color: #27ae60;
  margin-bottom: 15px;
  font-size: 0.9rem;
  background-color: rgba(39, 174, 96, 0.1);
  padding: 10px 15px;
  border-radius: 5px;
  border-left: 4px solid #27ae60;
`;

// Estilizar o componente reCAPTCHA
const RecaptchaContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
  
  .g-recaptcha {
    transform-origin: center;
    -webkit-transform-origin: center;
    
    @media (max-width: 320px) {
      transform: scale(0.85);
      -webkit-transform: scale(0.85);
    }
  }
`;

function App() {
  // Estado para controlar quais perguntas do FAQ estão abertas
  const [openFaqs, setOpenFaqs] = React.useState([]);
  
  // Estado para controlar a seção de agendamento (substitui o modal)
  const [isBookingSectionVisible, setIsBookingSectionVisible] = React.useState(false);
  
  // Estado para controlar o envio bem-sucedido do formulário
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  
  // Estado para controlar o menu mobile
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  
  // Novo estado para formulário de contato
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    user_message: '',
    service_type: '',
    preferred_date: '',
    preferred_time: ''
  });
  
  // Estado para status do formulário
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    error: null,
    success: false
  });
  
  // Adicionar o estado para o reCAPTCHA
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  
  // Inicializar EmailJS
  useEffect(() => {
    // Substitua 'SEU_USER_ID' pelo seu ID de usuário real
    emailjs.init('s0GmMLxtpR6DUhMhB');
    
    // Adicionar o callback do reCAPTCHA como função global
    window.handleRecaptchaVerify = () => {
      setRecaptchaVerified(true);
    };
    
    // Adicionar callback para quando o reCAPTCHA expirar
    window.handleRecaptchaExpired = () => {
      setRecaptchaVerified(false);
      console.log('reCAPTCHA expirou. Por favor, verifique novamente.');
    };
    
    // Cleanup function para remover a função global quando o componente for desmontado
    return () => {
      delete window.handleRecaptchaVerify;
      delete window.handleRecaptchaExpired;
    };
  }, []);
  
  // Função para atualizar os dados do formulário
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  // Função para alternar o estado aberto/fechado das perguntas do FAQ
  const toggleFaq = (index) => {
    setOpenFaqs(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };
  
  // Função para abrir a seção de agendamento
  const openBookingSection = () => {
    setIsBookingSectionVisible(true);
    setFormSubmitted(false);
    setMobileMenuOpen(false); // Fechar menu mobile
    document.body.style.overflow = 'hidden'; // Impedir rolagem da página
  };
  
  // Função para fechar a seção de agendamento
  const closeBookingSection = () => {
    setIsBookingSectionVisible(false);
    document.body.style.overflow = 'auto'; // Restaurar rolagem da página
    
    // Resetar o estado do formulário ao fechar
    setFormSubmitted(false);
    setFormData({
      user_name: '',
      user_email: '',
      user_phone: '',
      user_message: '',
      service_type: '',
      preferred_date: '',
      preferred_time: ''
    });
    setRecaptchaVerified(false);
    
    // Resetar o reCAPTCHA se existir e se estiver carregado completamente
    // Verificação mais completa para evitar erro "No reCAPTCHA clients exist"
    try {
      if (
        window.grecaptcha && 
        typeof window.grecaptcha.reset === 'function' &&
        document.querySelector('.g-recaptcha-response') // verifica se o reCAPTCHA foi realmente renderizado
      ) {
        window.grecaptcha.reset();
      }
    } catch (error) {
      console.log('ReCAPTCHA ainda não está pronto para reset');
    }
  };
  
  // Função para abrir/fechar o menu mobile
  const toggleMobileMenu = () => {
    setMobileMenuOpen(prev => !prev);
  };
  
  // Função para fechar o menu mobile ao clicar em um link
  const handleNavLinkClick = () => {
    setMobileMenuOpen(false);
  };
  
  // Adicionar a função de callback do reCAPTCHA
  const handleRecaptchaVerify = () => {
    setRecaptchaVerified(true);
  };
  
  // Função para simular o envio do formulário - Substitua por EmailJS
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Verificar se o reCAPTCHA foi respondido
    if (!recaptchaVerified) {
      setFormStatus({
        submitting: false,
        error: 'Por favor, complete a verificação reCAPTCHA antes de enviar o formulário.',
        success: false
      });
      return;
    }
    
    setFormStatus({
      submitting: true,
      error: null,
      success: false
    });
    
    // Configuração do EmailJS
    const serviceID = 'service_ojnlq4u'; // Substitua pelo seu Service ID
    const templateID = 'template_8wr4c4b'; // Substitua pelo seu Template ID
    
    // Envio do email usando EmailJS
    emailjs.send(serviceID, templateID, formData)
      .then((response) => {
        console.log('E-mail enviado com sucesso!', response.status, response.text);
        setFormStatus({
          submitting: false,
          error: null,
          success: true
        });
        setFormSubmitted(true);
        
        // Resetar o reCAPTCHA
        setRecaptchaVerified(false);
        try {
          if (
            window.grecaptcha && 
            typeof window.grecaptcha.reset === 'function' &&
            document.querySelector('.g-recaptcha-response')
          ) {
            window.grecaptcha.reset();
          }
        } catch (error) {
          console.log('ReCAPTCHA ainda não está pronto para reset');
        }
        
        // Rolando para o topo do formulário após o envio
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      })
      .catch((error) => {
        console.error('Erro ao enviar e-mail:', error);
        setFormStatus({
          submitting: false,
          error: 'Ocorreu um erro ao enviar sua mensagem. Por favor, tente novamente.',
          success: false
        });
      });
  };
  
  return React.createElement(
    AppContainer,
    null,
    // AddToAny script para compartilhamento social
    React.createElement('script', {
      async: true,
      src: 'https://static.addtoany.com/menu/page.js'
    }),
    // Cabeçalho com navegação semântica
    React.createElement(Header, null,
      React.createElement(Logo, null, "Rafaela Mendes Luz"),
      React.createElement(NavBar, { role: "navigation", "aria-label": "Navegação principal" },
        React.createElement(NavLink, { href: "#servicos", "aria-label": "Ir para seção de serviços" }, "Serviços"),
        React.createElement(NavLink, { href: "#sobre", "aria-label": "Ir para seção sobre mim" }, "Sobre"),
        React.createElement(NavLink, { href: "#especialidades", "aria-label": "Ir para especialidades" }, "Especialidades"),
        React.createElement(NavLink, { href: "#formacao", "aria-label": "Ir para formação" }, "Formação"),
        React.createElement(NavLink, { href: "#depoimentos", "aria-label": "Ir para depoimentos" }, "Depoimentos"),
        React.createElement(NavLink, { href: "#faq", "aria-label": "Ir para perguntas frequentes" }, "FAQ"),
        React.createElement('a', { 
          onClick: () => window.location.href = "https://psiluz.com.br/auth/login",
          "aria-label": "Acessar área restrita",
          style: { 
            marginLeft: '15px', 
            backgroundColor: '#5a9a94', 
            color: 'white', 
            padding: '8px 15px', 
            borderRadius: '4px',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: '0.9em',
            cursor: 'pointer'
          }
        }, "Área Restrita")
      ),
      // Botão para menu mobile
      React.createElement(MobileMenuButton, { 
        onClick: toggleMobileMenu,
        "aria-label": "Menu"
      }, mobileMenuOpen ? "×" : "☰"),
      
      // Menu mobile
      React.createElement(MobileMenu, { $isOpen: mobileMenuOpen },
        React.createElement(MobileNavLink, { href: "#sobre", onClick: handleNavLinkClick }, "Sobre"),
        React.createElement(MobileNavLink, { href: "#especialidades", onClick: handleNavLinkClick }, "Especialidades"),
        React.createElement(MobileNavLink, { href: "#formacao", onClick: handleNavLinkClick }, "Formação"),
        React.createElement(MobileNavLink, { href: "#servicos", onClick: handleNavLinkClick }, "Serviços"),
        React.createElement(MobileNavLink, { href: "#depoimentos", onClick: handleNavLinkClick }, "Depoimentos"),
        React.createElement(MobileNavLink, { href: "#faq", onClick: handleNavLinkClick }, "FAQ"),
        React.createElement(MobileNavLink, { href: "#contato", onClick: handleNavLinkClick }, "Contato"),
        React.createElement('a', { 
          onClick: () => window.location.href = "https://psiluz.com.br/auth/login",
          style: { 
            display: 'block',
            padding: '15px 20px',
            margin: '10px 20px',
            backgroundColor: '#5a9a94', 
            color: 'white',
            borderRadius: '4px',
            textAlign: 'center',
            textDecoration: 'none',
            fontWeight: 500,
            cursor: 'pointer'
          }
        }, "Área Restrita")
      )
    ),
    
    // Hero Section - Updated with semantic elements and ARIA attributes
    React.createElement(HeroSection, { role: "banner", "aria-labelledby": "hero-title" },
      React.createElement(HeroBgSVG, {
        dangerouslySetInnerHTML: {
          __html: `
            <svg width="100%" height="100%" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg">
              <!-- Ondas cerebrais estilizadas - versão melhorada -->
              <path d="M-100,450 C50,400 150,510 300,460 S500,410 650,460 S800,510 950,460 S1100,410 1250,460" 
                    fill="none" stroke="#84b59f" stroke-width="2" opacity="0.4" />
              <path d="M-100,480 C50,430 150,540 300,490 S500,440 650,490 S800,540 950,490 S1100,440 1250,490" 
                    fill="none" stroke="#5a9a94" stroke-width="2.5" opacity="0.5" />
              <path d="M-100,510 C50,460 150,570 300,520 S500,470 650,520 S800,570 950,520 S1100,470 1250,520" 
                    fill="none" stroke="#90c3be" stroke-width="2" opacity="0.4" />
                    
              <!-- Círculos abstratos e elegantes -->
              <circle cx="150" cy="200" r="100" fill="#5a9a94" opacity="0.07" />
              <circle cx="950" cy="650" r="150" fill="#84b59f" opacity="0.07" />
              <circle cx="850" cy="180" r="80" fill="#90c3be" opacity="0.07" />
              <circle cx="250" cy="650" r="120" fill="#5a9a94" opacity="0.07" />
              
              <!-- Meia mandala estilizada -->
              <path d="M950,200 A180,180 0 0,1 770,200 A180,180 0 0,1 950,200 Z" 
                    fill="none" stroke="#5a9a94" stroke-width="1.5" opacity="0.25" />
              <path d="M950,200 A150,150 0 0,1 800,200 A150,150 0 0,1 950,200 Z" 
                    fill="none" stroke="#84b59f" stroke-width="1.5" opacity="0.25" />
              <path d="M950,200 A120,120 0 0,1 830,200 A120,120 0 0,1 950,200 Z" 
                    fill="none" stroke="#90c3be" stroke-width="1.5" opacity="0.25" />
              
              <!-- Elementos decorativos sutis -->
              <path d="M150,700 Q200,650 250,700 T350,700" stroke="#5a9a94" stroke-width="2" fill="none" opacity="0.15" />
              <path d="M850,100 Q900,50 950,100 T1050,100" stroke="#84b59f" stroke-width="2" fill="none" opacity="0.15" />
              
              <!-- Símbolos abstratos de transformação e equilíbrio -->
              <path d="M650,300 m-60,0 a60,60 0 1,0 120,0 a60,60 0 1,0 -120,0" fill="none" stroke="#5a9a94" stroke-width="1.5" opacity="0.2" />
              <rect x="645" y="265" width="10" height="70" rx="5" fill="#5a9a94" opacity="0.2" />
              <rect x="645" y="265" width="10" height="70" rx="5" fill="#5a9a94" opacity="0.2" transform="rotate(90 650 300)" />
              
              <path d="M350,450 m-50,0 a50,50 0 1,0 100,0 a50,50 0 1,0 -100,0" fill="none" stroke="#84b59f" stroke-width="1.5" opacity="0.2" />
              <path d="M350,425 l-25,50 l50,0 z" fill="#84b59f" opacity="0.2" />
            </svg>
          `
        }
      }),
      React.createElement(HeroDecoration, null,
        React.createElement(HeroDecorationInner, null,
          React.createElement(HeroDecorationCore, null)
        )
      ),
      React.createElement(HeroContent, null,
        React.createElement(HeroTitle, { id: "hero-title" }, "Jornada de Autoconhecimento & Transformação"),
        React.createElement(HeroSubtitle, null, "Como psicóloga especializada em Psicologia Analítica, ofereço um espaço seguro e acolhedor para sua jornada de desenvolvimento pessoal. Minha abordagem considera os aspectos conscientes e inconscientes da sua psique, promovendo crescimento, bem-estar emocional e um caminho para sua verdadeira individualidade."),
        React.createElement(Button, { 
          onClick: openBookingSection,
          style: { 
            background: 'linear-gradient(135deg, #5a9a94 0%, #84b59f 100%)', 
            border: 'none', 
            color: 'white',
            boxShadow: '0 10px 20px rgba(90, 154, 148, 0.3)'
          } 
        }, "Agende sua Consulta")
      )
    ),
    
    // Services Section
    React.createElement(ServicesSection, { id: "servicos" },
      React.createElement(ServicesBgSVG, {
        dangerouslySetInnerHTML: {
          __html: `
            <svg width="100%" height="100%" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg">
              <!-- Padrões de conexão neuronal -->
              <g opacity="0.1">
                <circle cx="100" cy="100" r="8" fill="#5a9a94" />
                <circle cx="300" cy="150" r="8" fill="#5a9a94" />
                <circle cx="200" cy="250" r="8" fill="#5a9a94" />
                <circle cx="450" cy="100" r="8" fill="#5a9a94" />
                <circle cx="550" cy="200" r="8" fill="#5a9a94" />
                <circle cx="750" cy="150" r="8" fill="#5a9a94" />
                <circle cx="850" cy="250" r="8" fill="#5a9a94" />
                <circle cx="950" cy="150" r="8" fill="#5a9a94" />
                <circle cx="1050" cy="250" r="8" fill="#5a9a94" />
                
                <line x1="100" y1="100" x2="300" y2="150" stroke="#84b59f" stroke-width="2" />
                <line x1="300" y1="150" x2="200" y2="250" stroke="#84b59f" stroke-width="2" />
                <line x1="300" y1="150" x2="450" y2="100" stroke="#84b59f" stroke-width="2" />
                <line x1="450" y1="100" x2="550" y2="200" stroke="#84b59f" stroke-width="2" />
                <line x1="550" y1="200" x2="750" y2="150" stroke="#84b59f" stroke-width="2" />
                <line x1="750" y1="150" x2="850" y2="250" stroke="#84b59f" stroke-width="2" />
                <line x1="850" y1="250" x2="950" y2="150" stroke="#84b59f" stroke-width="2" />
                <line x1="950" y1="150" x2="1050" y2="250" stroke="#84b59f" stroke-width="2" />
              </g>
              
              <!-- Símbolos abstratos de crescimento -->
              <g opacity="0.1" transform="translate(100, 400)">
                <path d="M0,100 Q50,50 100,100 T200,100" stroke="#5a9a94" stroke-width="3" fill="none" />
                <path d="M0,150 Q50,100 100,150 T200,150" stroke="#84b59f" stroke-width="3" fill="none" />
                <path d="M0,200 Q50,150 100,200 T200,200" stroke="#90c3be" stroke-width="3" fill="none" />
              </g>
              
              <g opacity="0.1" transform="translate(800, 500)">
                <path d="M0,100 Q50,50 100,100 T200,100" stroke="#5a9a94" stroke-width="3" fill="none" />
                <path d="M0,150 Q50,100 100,150 T200,150" stroke="#84b59f" stroke-width="3" fill="none" />
                <path d="M0,200 Q50,150 100,200 T200,200" stroke="#90c3be" stroke-width="3" fill="none" />
              </g>
            </svg>
          `
        }
      }),
      React.createElement(SectionHeading, null,
        React.createElement(SectionSubtitle, null, "Serviços"),
        React.createElement(SectionTitle, null, "Modalidades de Atendimento")
      ),
      React.createElement(ServicesContainer, null,
        React.createElement(ServiceCard, null,
          React.createElement(ServiceIcon, null, "🏢"),
          React.createElement(ServiceTitle, null, "Atendimento Presencial"),
          React.createElement(ServiceDescription, null, "Sessões realizadas em consultório na região da Vila Mariana em São Paulo, em ambiente acolhedor, confortável e privativo para seu processo terapêutico.")
        ),
        React.createElement(ServiceCard, null,
          React.createElement(ServiceIcon, null, "💻"),
          React.createElement(ServiceTitle, null, "Atendimento Online"),
          React.createElement(ServiceDescription, null, "Sessões virtuais com a mesma qualidade do atendimento presencial, proporcionando flexibilidade e comodidade para realizar sua terapia de onde estiver.")
        ),
        React.createElement(ServiceCard, null,
          React.createElement(ServiceIcon, null, "📆"),
          React.createElement(ServiceTitle, null, "Primeira Consulta"),
          React.createElement(ServiceDescription, null, "Sessão inicial para conhecer sua história, necessidades específicas e estabelecer objetivos terapêuticos, criando as bases para um processo terapêutico personalizado.")
        )
      )
    ),
    
    // About Section
    React.createElement(AboutSection, { id: "sobre" },
      React.createElement(AboutBgSVG, {
        dangerouslySetInnerHTML: {
          __html: `
            <svg width="100%" height="100%" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg">
              <!-- Formas orgânicas representando crescimento e desenvolvimento -->
              <path d="M-50,250 C100,200 250,350 350,250 S500,100 650,150 S800,250 1000,200 S1200,100 1350,150" 
                    fill="none" stroke="#84b59f" stroke-width="2" opacity="0.2" />
                    
              <!-- Forma abstrata simbolizando a mente -->
              <path d="M800,500 C950,450 1050,650 1200,600" 
                    fill="none" stroke="#5a9a94" stroke-width="3" opacity="0.2" />
              <path d="M750,550 C900,500 1000,700 1150,650" 
                    fill="none" stroke="#90c3be" stroke-width="2" opacity="0.2" />
              
              <!-- Folhas/elementos orgânicos para simbolizar crescimento -->
              <path d="M100,600 Q150,550 100,500 Q50,550 100,600 Z" fill="#84b59f" opacity="0.1" />
              <path d="M150,650 Q200,600 150,550 Q100,600 150,650 Z" fill="#5a9a94" opacity="0.1" />
              <path d="M200,700 Q250,650 200,600 Q150,650 200,700 Z" fill="#90c3be" opacity="0.1" />
              
              <!-- Círculos decorativos -->
              <circle cx="900" cy="300" r="50" fill="#5a9a94" opacity="0.1" />
              <circle cx="950" cy="350" r="30" fill="#84b59f" opacity="0.1" />
              <circle cx="1000" cy="320" r="20" fill="#90c3be" opacity="0.1" />
              
              <!-- Elemento decorativo abstrato representando a psicologia -->
              <path d="M250,100 C300,150 350,100 400,150" 
                    fill="none" stroke="#5a9a94" stroke-width="3" opacity="0.15" />
            </svg>
          `
        }
      }),
      React.createElement(ImageContainer, null,
        React.createElement(AboutImageDecoration1, null),
        React.createElement(AboutImageDecoration2, null),
        React.createElement(AboutImage, { $hasImage: true },
          React.createElement('img', { src: "/rafa.jpeg", alt: "Psicóloga Rafaela Mendes Luz" })
        )
      ),
      React.createElement(AboutContent, null,
        React.createElement(SectionSubtitle, null, "Sobre Mim"),
        React.createElement(AboutTitle, null, "Psicóloga Especialista"),
        React.createElement(AboutText, null, "Sou psicóloga clínica especializada em Psicologia Analítica, formada em 2018 pela Universidade Paulista (UNIP). Desenvolvi sólida experiência em diversos contextos, incluindo atendimento hospitalar, familiar e individual."),
        React.createElement(AboutText, null, "Minha abordagem terapêutica busca promover uma compreensão profunda do indivíduo, considerando os aspectos conscientes e inconscientes da psique. Acredito no poder da psicoterapia como ferramenta de transformação e autoconhecimento, criando um ambiente seguro e acolhedor para explorar desafios e potenciais de crescimento."),
        React.createElement(Button, { href: "#contato", style: { background: 'var(--gradient-primary)', border: 'none' } }, "Entre em Contato")
      )
    ),
    
    // Specialties Section
    React.createElement(SpecialtiesSection, { id: "especialidades" },
      React.createElement(SpecialtiesBgSVG, {
        dangerouslySetInnerHTML: {
          __html: `
            <svg width="100%" height="100%" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg">
              <path d="M-100,300 C100,200 300,400 500,300 S700,200 900,300 S1100,400 1300,300" 
                    fill="none" stroke="#90c3be" stroke-width="2" opacity="0.2" />
              <circle cx="200" cy="200" r="100" fill="#5a9a94" opacity="0.05" />
              <circle cx="900" cy="600" r="150" fill="#84b59f" opacity="0.05" />
            </svg>
          `
        }
      }),
      React.createElement(SectionHeading, null,
        React.createElement(SectionSubtitle, null, "Especialidades"),
        React.createElement(SectionTitle, null, "Áreas de Atuação")
      ),
      React.createElement(SpecialtiesContainer, null,
        React.createElement(SpecialtyCard, null,
          React.createElement(SpecialtyIcon, null, "🧠"),
          React.createElement(SpecialtyTitle, null, "Psicologia Analítica"),
          React.createElement(SpecialtyDescription, null, "Abordagem baseada nos conceitos de Carl Jung, que considera aspectos conscientes e inconscientes da psique, promovendo autoconhecimento e transformação pessoal.")
        ),
        React.createElement(SpecialtyCard, null,
          React.createElement(SpecialtyIcon, null, "👨‍👩‍👧"),
          React.createElement(SpecialtyTitle, null, "Terapia Familiar"),
          React.createElement(SpecialtyDescription, null, "Trabalho com dinâmicas e comunicação familiar, auxiliando no fortalecimento de vínculos, melhorando a resolução de conflitos e promovendo relacionamentos saudáveis.")
        ),
        React.createElement(SpecialtyCard, null,
          React.createElement(SpecialtyIcon, null, "👦👧"),
          React.createElement(SpecialtyTitle, null, "Psicoterapia de Adolescentes"),
          React.createElement(SpecialtyDescription, null, "Atendimento especializado para adolescentes, auxiliando-os a lidar com desafios emocionais, sociais e na construção de identidade durante esta importante fase de transição.")
        ),
        React.createElement(SpecialtyCard, null,
          React.createElement(SpecialtyIcon, null, "😰"),
          React.createElement(SpecialtyTitle, null, "Ansiedade e Depressão"),
          React.createElement(SpecialtyDescription, null, "Acompanhamento para questões de ansiedade, estresse, autoestima e depressão, com foco no desenvolvimento de estratégias de enfrentamento e bem-estar emocional.")
        )
      )
    ),
    
    // Formação Acadêmica
    React.createElement(EducationSection, { id: "formacao" },
      React.createElement(SectionHeading, null,
        React.createElement(SectionSubtitle, null, "Formação"),
        React.createElement(SectionTitle, null, "Qualificações Acadêmicas")
      ),
      React.createElement(EducationContainer, null,
        React.createElement(EducationItem, null,
          React.createElement(EducationYear, null, "2018"),
          React.createElement(EducationDegree, null, "Graduação em Psicologia"),
          React.createElement(EducationInstitution, null, "Universidade Paulista (UNIP)"),
          React.createElement(EducationDescription, null, "Formação completa em Psicologia com ênfase em psicologia clínica e desenvolvimento humano.")
        ),
        React.createElement(EducationItem, null,
          React.createElement(EducationYear, null, "2019"),
          React.createElement(EducationDegree, null, "Formação em Psicoterapia de Adolescentes"),
          React.createElement(EducationInstitution, null, "Centro de Estudos Junguianos"),
          React.createElement(EducationDescription, null, "Especialização em técnicas terapêuticas para atendimento de adolescentes, baseadas na psicologia analítica e no desenvolvimento da personalidade durante a adolescência.")
        ),
        React.createElement(EducationItem, null,
          React.createElement(EducationYear, null, "2020"),
          React.createElement(EducationDegree, null, "Especialização em Psicologia Analítica"),
          React.createElement(EducationInstitution, null, "Instituto Jung"),
          React.createElement(EducationDescription, null, "Especialização nos conceitos e práticas da Psicologia Analítica desenvolvida por Carl Jung, focando no estudo do inconsciente coletivo, arquétipos e processo de individuação.")
        ),
        React.createElement(EducationItem, null,
          React.createElement(EducationYear, null, "2021"),
          React.createElement(EducationDegree, null, "Formação em Terapia Familiar Sistêmica"),
          React.createElement(EducationInstitution, null, "Instituto de Terapia Familiar de São Paulo"),
          React.createElement(EducationDescription, null, "Estudo das dinâmicas familiares e técnicas de intervenção para fortalecimento de vínculos e melhoria da comunicação entre os membros da família.")
        )
      )
    ),
    
    // Testimonials Section
    React.createElement(TestimonialsSection, { id: "depoimentos" },
      React.createElement(TestimonialsBgSVG, {
        dangerouslySetInnerHTML: {
          __html: `
            <svg width="100%" height="100%" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg">
              <path d="M-100,500 C100,450 300,550 500,500 S700,450 900,500 S1100,550 1300,500" 
                    fill="none" stroke="#84b59f" stroke-width="3" opacity="0.1" />
              <path d="M-100,600 C100,550 300,650 500,600 S700,550 900,600 S1100,650 1300,600" 
                    fill="none" stroke="#5a9a94" stroke-width="2" opacity="0.1" />
            </svg>
          `
        }
      }),
      React.createElement(SectionHeading, null,
        React.createElement(SectionSubtitle, null, "Depoimentos"),
        React.createElement(SectionTitle, null, "O que dizem meus pacientes")
      ),
      // Substituir pelo widget do Elfsight
      React.createElement(GoogleReviewsWidget, null,
        React.createElement('div', {
          className: 'elfsight-app-3ca62449-210d-4a45-88cd-64b7925aecda',
          'data-elfsight-app-lazy': true
        })
      ),
      // Adicionar o script do Elfsight
      React.createElement('script', {
        src: 'https://static.elfsight.com/platform/platform.js',
        async: true
      })
    ),
    
    // FAQ Section
    React.createElement(FaqSection, { id: "faq" },
      React.createElement(SectionHeading, null,
        React.createElement(SectionSubtitle, null, "Dúvidas"),
        React.createElement(SectionTitle, null, "Perguntas Frequentes")
      ),
      React.createElement(FaqContainer, null,
        React.createElement(FaqItem, null,
          React.createElement(FaqQuestion, { 
            onClick: () => toggleFaq(0),
            $isOpen: openFaqs.includes(0)
          }, "Quanto tempo dura uma sessão de terapia?"),
          React.createElement(FaqAnswer, { $isOpen: openFaqs.includes(0) },
            "As sessões têm duração de 50 minutos, o que é considerado o padrão para atendimentos psicológicos. Este tempo foi estabelecido para proporcionar um equilíbrio adequado entre profundidade terapêutica e capacidade de processamento emocional."
          )
        ),
        React.createElement(FaqItem, null,
          React.createElement(FaqQuestion, { 
            onClick: () => toggleFaq(1),
            $isOpen: openFaqs.includes(1)
          }, "Com que frequência devo fazer terapia?"),
          React.createElement(FaqAnswer, { $isOpen: openFaqs.includes(1) },
            "A frequência recomendada inicialmente é semanal, para estabelecer um vínculo terapêutico adequado e permitir um acompanhamento consistente do seu progresso. Com o tempo, conforme os objetivos terapêuticos forem sendo atingidos, a frequência pode ser ajustada para quinzenal ou mensal."
          )
        ),
        React.createElement(FaqItem, null,
          React.createElement(FaqQuestion, { 
            onClick: () => toggleFaq(2),
            $isOpen: openFaqs.includes(2)
          }, "A terapia online é tão eficaz quanto a presencial?"),
          React.createElement(FaqAnswer, { $isOpen: openFaqs.includes(2) },
            "Sim, estudos recentes mostram que a terapia online pode ser tão eficaz quanto a presencial para muitas condições. Oferece a conveniência de poder fazer as sessões de qualquer lugar, economizando tempo de deslocamento. Além disso, algumas pessoas se sentem mais confortáveis se abrindo em um ambiente familiar."
          )
        ),
        React.createElement(FaqItem, null,
          React.createElement(FaqQuestion, { 
            onClick: () => toggleFaq(3),
            $isOpen: openFaqs.includes(3)
          }, "Como sei se preciso de terapia?"),
          React.createElement(FaqAnswer, { $isOpen: openFaqs.includes(3) },
            "A terapia pode ser benéfica para qualquer pessoa que esteja enfrentando desafios emocionais, comportamentais ou relacionais que afetam sua qualidade de vida. Sinais como tristeza persistente, ansiedade, dificuldades nos relacionamentos, mudanças no sono ou apetite, ou sentimentos de estar sobrecarregado são indicadores de que a terapia pode ajudar."
          )
        ),
        React.createElement(FaqItem, null,
          React.createElement(FaqQuestion, { 
            onClick: () => toggleFaq(4),
            $isOpen: openFaqs.includes(4)
          }, "Os atendimentos são sigilosos?"),
          React.createElement(FaqAnswer, { $isOpen: openFaqs.includes(4) },
            "Absolutamente. O sigilo profissional é um princípio ético fundamental na psicologia. Todas as informações compartilhadas nas sessões são confidenciais, protegidas pelo Código de Ética Profissional do Psicólogo. Exceções só existem em situações específicas previstas por lei, como risco de vida para o paciente ou terceiros."
          )
        ),
        React.createElement(FaqItem, null,
          React.createElement(FaqQuestion, { 
            onClick: () => toggleFaq(5),
            $isOpen: openFaqs.includes(5)
          }, "Qual abordagem terapêutica você utiliza?"),
          React.createElement(FaqAnswer, { $isOpen: openFaqs.includes(5) },
            "Trabalho principalmente com a Psicologia Analítica, desenvolvida por Carl Jung. Essa abordagem busca compreender o indivíduo considerando os aspectos conscientes e inconscientes da psique, utilizando conceitos como arquétipos, inconsciente coletivo e individuação. O processo terapêutico é adaptado às necessidades individuais de cada pessoa."
          )
        ),
        React.createElement(FaqItem, null,
          React.createElement(FaqQuestion, { 
            onClick: () => toggleFaq(6),
            $isOpen: openFaqs.includes(6)
          }, "Você atende adolescentes e famílias?"),
          React.createElement(FaqAnswer, { $isOpen: openFaqs.includes(6) },
            "Sim, ofereço atendimento para diferentes faixas etárias, incluindo adolescentes, adultos e famílias. Na terapia com adolescentes, utilizo abordagens específicas para auxiliá-los nessa fase de transição. Na terapia familiar, trabalho com dinâmicas e comunicação para fortalecer vínculos e promover relacionamentos saudáveis."
          )
        )
      )
    ),
    
    // Contact Section
    React.createElement(ContactSection, { id: "contato" },
      React.createElement(SectionHeading, null,
        React.createElement(SectionSubtitle, { style: { color: 'rgba(255,255,255,0.8)' } }, "Contato"),
        React.createElement(ContactTitle, null, "Estou aqui para você")
      ),
      React.createElement(InfoContainer, null,
        React.createElement(InfoCard, null,
          React.createElement(InfoIcon, null, React.createElement(FaInstagram, null)),
          React.createElement(InfoTitle, null, "Instagram"),
          React.createElement(InfoText, null, "@rafaelabamen")
        ),
        React.createElement(InfoCard, null,
          React.createElement(InfoIcon, null, React.createElement(FaEnvelope, null)),
          React.createElement(InfoTitle, null, "E-mail"),
          React.createElement(InfoText, null, "rafaelapsico@outlook.com")
        ),
        React.createElement(InfoCard, null,
          React.createElement(InfoIcon, null, React.createElement(FaMapMarkerAlt, null)),
          React.createElement(InfoTitle, null, "Localização"),
          React.createElement(InfoText, null, "Vila Mariana, São Paulo - SP")
        ),
        React.createElement(InfoCard, null,
          React.createElement(InfoIcon, null, React.createElement(FaClock, null)),
          React.createElement(InfoTitle, null, "Horário de Atendimento"),
          React.createElement(InfoText, null, "Segunda à Sexta: 9:00 - 18:00")
        )
      )
    ),
    
    // CTA Section
    React.createElement(CtaSection, null,
      React.createElement(CtaTitle, null, "Pronto para iniciar seu processo de autoconhecimento?"),
      React.createElement(CtaText, null, "Dê o primeiro passo em direção ao seu bem-estar emocional. Estou aqui para acompanhar sua jornada de transformação pessoal e desenvolvimento da sua individualidade."),
      React.createElement(CtaButton, { 
        onClick: openBookingSection
      }, "Agende sua Consulta")
    ),
    
    // Footer
    React.createElement(Footer, null,
      React.createElement(FooterLogo, null, "Rafaela Mendes Luz"),
      React.createElement(FooterText, null, "Psicóloga clínica especializada em Psicologia Analítica, dedicada a promover autoconhecimento e transformação pessoal através de um atendimento humanizado e acolhedor."),
      React.createElement(SocialIcons, null,
        React.createElement("a", { href: "https://instagram.com/rafaelabamen", target: "_blank", rel: "noopener noreferrer" }, 
          React.createElement(FaInstagram, null)
        ),
        React.createElement("a", { href: "mailto:rafaelapsico@outlook.com" }, 
          React.createElement(FaEnvelope, null)
        )
      ),
      React.createElement(Copyright, null, `© ${new Date().getFullYear()} Psicóloga Rafaela Mendes Luz. Todos os direitos reservados. CRP: 06/174880`)
    ),
    
    // Botão Flutuante de WhatsApp
    React.createElement(WhatsAppButton, { 
      href: "https://wa.me/5511984883825?text=Ol%C3%A1%2C%20acessei%20seu%20site%20e%20gostaria%20de%20saber%20mais%20sobre%20seus%20servi%C3%A7os%20de%20psicologia.%20Poder%C3%ADamos%20agendar%20uma%20consulta%3F!", 
      target: "_blank",
      rel: "noopener noreferrer",
      "aria-label": "Contato via WhatsApp"
    },
      React.createElement(FaWhatsapp, null),
      React.createElement(WhatsAppTooltip, null, "Agendar Consulta")
    ),
    
    // Seção de Agendamento (substitui o modal)
    React.createElement(BookingSection, { $isVisible: isBookingSectionVisible },
      React.createElement(BookingContainer, null,
        React.createElement(BookingFormHeader, null,
          React.createElement(BackButton, { onClick: closeBookingSection },
            React.createElement(BackArrow, null, "←"),
            "Voltar"
          ),
          React.createElement(BookingFormTitle, null, "Agende sua Consulta")
        ),
        
        !formSubmitted ? (
          React.createElement(BookingForm, { onSubmit: handleSubmit },
            React.createElement(FormGroup, null,
              React.createElement(Label, null, "Nome completo"),
              React.createElement(Input, { 
                type: "text", 
                name: "user_name",
                value: formData.user_name,
                onChange: handleInputChange,
                placeholder: "Digite seu nome", 
                required: true,
                autoFocus: true
              })
            ),
            React.createElement(FormGroup, null,
              React.createElement(Label, null, "E-mail para contato"),
              React.createElement(Input, { 
                type: "email", 
                name: "user_email",
                value: formData.user_email,
                onChange: handleInputChange,
                placeholder: "exemplo@email.com", 
                required: true 
              })
            ),
            React.createElement(FormGroup, null,
              React.createElement(Label, null, "Telefone/WhatsApp"),
              React.createElement(Input, { 
                type: "tel", 
                name: "user_phone",
                value: formData.user_phone,
                onChange: handleInputChange,
                placeholder: "(11) 99999-9999", 
                required: true 
              })
            ),
            React.createElement(FormGroup, null,
              React.createElement(Label, null, "Modalidade de Atendimento"),
              React.createElement(Select, { 
                name: "service_type",
                value: formData.service_type,
                onChange: handleInputChange,
                required: true 
              },
                React.createElement("option", { value: "" }, "Selecione..."),
                React.createElement("option", { value: "presencial" }, "Presencial"),
                React.createElement("option", { value: "online" }, "Online")
              )
            ),
            React.createElement(FormGroup, null,
              React.createElement(Label, null, "Data Preferencial"),
              React.createElement(Input, { 
                type: "date", 
                name: "preferred_date",
                value: formData.preferred_date,
                onChange: handleInputChange,
                required: true 
              })
            ),
            React.createElement(FormGroup, null,
              React.createElement(Label, null, "Horário Preferencial"),
              React.createElement(Select, { 
                name: "preferred_time",
                value: formData.preferred_time,
                onChange: handleInputChange,
                required: true 
              },
                React.createElement("option", { value: "" }, "Selecione..."),
                React.createElement("option", { value: "manha" }, "Manhã (9h - 12h)"),
                React.createElement("option", { value: "tarde" }, "Tarde (13h - 18h)")
              )
            ),
            React.createElement(FormGroup, null,
              React.createElement(Label, null, "Alguma informação adicional? (opcional)"),
              React.createElement(Textarea, { 
                name: "user_message",
                value: formData.user_message,
                onChange: handleInputChange,
                placeholder: "Compartilhe brevemente o motivo de sua consulta ou quaisquer informações que considere relevantes." 
              })
            ),
            // Adicionar o reCAPTCHA
            React.createElement(RecaptchaContainer, null,
              React.createElement('div', { 
                className: 'g-recaptcha',
                'data-sitekey': '6LcV7fkqAAAAAAzMxrwWpQ9IWlfCugGZ5Jy_Bz3i',
                'data-callback': 'handleRecaptchaVerify',
                'data-expired-callback': 'handleRecaptchaExpired'
              })
            ),
            formStatus.error && React.createElement(FormError, null, formStatus.error),
            formStatus.success && React.createElement(FormSuccess, null, "Sua mensagem foi enviada com sucesso!"),
            React.createElement(SubmitButton, { 
              type: "submit", 
              disabled: formStatus.submitting 
            }, formStatus.submitting ? "Enviando..." : "Solicitar Agendamento")
          )
        ) : (
          React.createElement(SuccessMessage, null,
            React.createElement("p", null, "Sua solicitação foi enviada com sucesso! Entrarei em contato em breve para confirmar o agendamento."),
            React.createElement(SubmitButton, { onClick: closeBookingSection }, "Voltar ao site")
          )
        )
      )
    )
  );
}

export default App;
